import React, { useEffect, useState } from "react";
import './bus-location.style.scss';
import { getPickupLocationByTripId, getStopArrival } from "./bus-location.module";
import BusLocationMapComponent from "../standard-stops/map-component/bus-location-map.component";
import { isValid, isValidArray } from "../../utils/utilities";
import { Divider } from "antd";
import { useDispatch } from "react-redux";
import { loadingAction } from "../../actions/loading-actions";
import moment from "moment";
import BusIcon from "../../images/bus-icon.svg";


export const BusLocationComponent = (props) => {

    const {
        tripId,
        scheduleId
    } = props;

    const [trackerData, setTrackerData] = useState({});
    const [stopArrivalData, setStopArrivalData] = useState([]);
    const [showStops, setShowStops] = useState(false);

    const dispatch = useDispatch();
    const setLoading = (bool) => {
        dispatch(loadingAction(bool));
    }

    useEffect(()=>{        
        if (isValid(global.travelsAppConfig) && isValid(global.travelsAppConfig.SHOW_ARRIVAL_TIMES) && global.travelsAppConfig.SHOW_ARRIVAL_TIMES == 1) {
            setShowStops(true);
        }
    },[]);

    useEffect(() => {
        if (tripId) {
            setLoading(true);
            
            // Fetch tracking data
            getPickupLocationByTripId(tripId, (trackingInfo) => {
                setTrackerData(trackingInfo);
            }, () => {});

            // Fetch stop arrival data
            if (scheduleId) {
                getStopArrival(tripId, scheduleId, (stopData) => {
                    setStopArrivalData(stopData);
                    setLoading(false);
                }, () => {
                    setLoading(false);
                });
            } else {
                setLoading(false);
            }
        }
    }, [tripId, scheduleId]);

    const getStatusBadge = (stop) => {
        // Use delayInMinutes from the API response
        if (stop.delayInMinutes === undefined || stop.delayInMinutes === null) return null;

        const delayMinutes = stop.delayInMinutes;

        if (Math.abs(delayMinutes) <= 5) {
            return <span className="status-badge on-time">On Time</span>;
        } else if (delayMinutes > 0) {
            // Positive delay means early
            return <span className="status-badge early">Early</span>;
        } else {
            // Negative delay means late/delay
            return <span className="status-badge delay">Delay</span>;
        }
    };

    const formatTime = (time) => {
        return moment(time, 'HH:mm:ss').format('hh:mm A');
    };

    const formatArrivalTime = (arrivalTime) => {
        if (!arrivalTime) return '-';
        // Convert arrivalTime (HH:mm:ss format) to 12-hour format
        return moment(arrivalTime, 'HH:mm:ss').format('hh:mm A');
    };

    const formatEtaTime = (etaTime) => {
        if (!etaTime) return '-';
        // Convert etaTime (HH:mm:ss format) to 12-hour format
        return moment(etaTime, 'HH:mm:ss').format('hh:mm A');
    };

    const formatDelayMinutes = (delayMinutes) => {
        if (delayMinutes === undefined || delayMinutes === null) return '-';
        
        const absDiff = Math.abs(delayMinutes);
        const hours = Math.floor(absDiff / 60);
        const mins = absDiff % 60;
        
        // Format as HH:MM
        const formattedTime = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
        
        // Add sign prefix for clarity
        if (delayMinutes > 0) {
            return `+${formattedTime}`;
        } else if (delayMinutes < 0) {
            return `-${formattedTime}`;
        }
        
        return formattedTime;
    };

    const shouldShowBusIcon = (cityIndex, stopIndex) => {
        // Find all stops across all cities
        let allStops = [];
        stopArrivalData.forEach((city, cIdx) => {
            city.stops.forEach((stop, sIdx) => {
                allStops.push({
                    cityIndex: cIdx,
                    stopIndex: sIdx,
                    hasArrival: !!stop.arrivalTime,
                    hasEta: !!stop.etaTime
                });
            });
        });

        // Find the last stop with arrival time
        let lastArrivalIndex = -1;
        for (let i = allStops.length - 1; i >= 0; i--) {
            if (allStops[i].hasArrival) {
                lastArrivalIndex = i;
                break;
            }
        }

        // Find the first stop with ETA time
        let firstEtaIndex = -1;
        for (let i = 0; i < allStops.length; i++) {
            if (allStops[i].hasEta) {
                firstEtaIndex = i;
                break;
            }
        }

        // Find current stop index in the flattened array
        let currentIndex = 0;
        for (let i = 0; i < cityIndex; i++) {
            currentIndex += stopArrivalData[i].stops.length;
        }
        currentIndex += stopIndex;

        // Show bus icon after the last arrival and before first ETA
        return currentIndex === lastArrivalIndex && firstEtaIndex > lastArrivalIndex;
    };

    const renderStopTimeline = () => {
        if (!isValidArray(stopArrivalData) || stopArrivalData.length === 0) {
            return (
                <div className="no-data">
                    No stop arrival data available
                </div>
            );
        }

        return (
            <div className="stop-timeline">
                <div className="timeline-header">
                    <div className="header-col">Stop Location</div>
                    <div className="header-col">Sched. Time</div>
                    <div className="header-col">Arrival Time</div>
                    <div className="header-col">ETA/Status</div>
                </div>

                {stopArrivalData.map((city, cityIndex) => {
                    // Check if any stop in this city is crossed (for city number indicator and header line)
                    const hasCrossedStops = city.stops && city.stops.some(stop => stop.crossed);
                    
                    return (
                    <div key={cityIndex} className={`city-group ${hasCrossedStops ? 'has-crossed-stops' : ''}`}>
                        <div className="city-header">
                            <div className={`city-number ${hasCrossedStops ? 'crossed' : ''}`}>{cityIndex + 1}</div>
                            <div className="city-info">
                                <h3 className="city-name">{city.displayName}</h3>
                                <div className="city-badges">
                                    {city.isBoarding && (() => {
                                        const totalPickup = city.stops.reduce((sum, s) => sum + (s.pickupCount || 0), 0);
                                        return totalPickup > 0 && (
                                            <span className="badge boarding">Boarding {totalPickup}</span>
                                        );
                                    })()}
                                    {city.isDrop && (() => {
                                        const totalDrop = city.stops.reduce((sum, s) => sum + (s.dropCount || 0), 0);
                                        return totalDrop > 0 && (
                                            <span className="badge dropping">Dropping {totalDrop}</span>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                        <div className="stops-container">
                            {city.stops && city.stops.map((stop, stopIndex) => (
                                <div key={stopIndex} className={`stop-row ${stop.crossed ? 'crossed' : ''}`}>
                                    <div className="stop-timeline-line">
                                        <div className="timeline-dot">
                                            {stop.crossed ? (
                                                <div className="main-stop-dot"></div>
                                            ) : (
                                                <div className="sub-stop-dot"></div>
                                            )}
                                        </div>
                                        {/* Show bus icon on the continuous timeline */}
                                        {shouldShowBusIcon(cityIndex, stopIndex) && (
                                            <div className="bus-on-route">
                                                <img src={BusIcon} alt="Bus Current Position" className="bus-icon" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="stop-content">
                                        <div className="stop-info">
                                            <div className="stop-name-col">
                                                <div className="stop-name-wrapper">
                                                    <span className="stop-name">{stop.displayName}</span>
                                                    {stop.isBoarding && stop.pickupCount > 0 && (
                                                        <span className="count-badge boarding-count">{stop.pickupCount}</span>
                                                    )}
                                                    {stop.isDrop && stop.dropCount > 0 && (
                                                        <span className="count-badge drop-count">{stop.dropCount}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="stop-time-col">
                                                <span className="scheduled-time">
                                                    {formatTime(stop.timing)}
                                                </span>
                                                {stop.day > 0 && (
                                                    <span className="day-badge">Day {stop.day+1}</span>
                                                )}
                                            </div>
                                            <div className="stop-arrival-col">
                                                <span className={`arrival-time ${stop.arrivalTime && (stop.delayInMinutes === undefined || stop.delayInMinutes === null || Math.abs(stop.delayInMinutes) <= 360) ? 'has-arrival' : ''}`}>
                                                    {(stop.arrivalTime && (stop.delayInMinutes === undefined || stop.delayInMinutes === null || Math.abs(stop.delayInMinutes) <= 360)) ? formatArrivalTime(stop.arrivalTime) : '-'}
                                                </span>
                                            </div>
                                            <div className="stop-eta-status-col">
                                                {!stop.etaTime && (stop.delayInMinutes === undefined || stop.delayInMinutes === null || Math.abs(stop.delayInMinutes) > 360) ? (
                                                    <span className="">-</span>
                                                ) : (
                                                    <>
                                                        {stop.etaTime ? (
                                                            <span className={`eta-time has-eta`}>
                                                                {formatEtaTime(stop.etaTime)}
                                                            </span>
                                                        ):
                                                        (stop.delayInMinutes !== undefined && stop.delayInMinutes !== null && Math.abs(stop.delayInMinutes) <= 360) && getStatusBadge(stop)
                                                        }
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="tab-block">
            <div className="search-bus-location-tab-root">
                {/* Stop Timeline Section */}
                {showStops &&
                    <div className="stops-section">
                        <div className="section-header">
                            <h2>Stops</h2>
                        </div>

                        {showStops && renderStopTimeline()}
                    </div>
                }
                {/* Map Section */}
                {isValid(trackerData) && isValid(trackerData.latitude) && isValid(trackerData.longitude) && (
                    <div className="map-section">
                        <BusLocationMapComponent 
                            latitude={trackerData.latitude} 
                            longitude={trackerData.longitude}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
